import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Users, X, Mail, Phone, ChevronRight, Trash2, Download, Search } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  useLeadsList,
  useDeleteLead,
  useUpdateLead,
  useDistinctLeadSources,
  fetchLeadsForExport,
} from '~/lib/queries';
import { useTenantContext, useActiveTenantId } from '~/lib/tenantContext';
import { useDashboardPeriod, resolvePeriod, tenantTimeZone } from '~/lib/dashboard-period';
import { PeriodFilter } from '~/components/PeriodFilter/PeriodFilter';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { currentUser } from '~/lib/pb';
import { can } from '~/lib/permissions';
import type { Lead } from '~/lib/types';
import styles from './_authed.oportunidades-bandeja.module.css';

// Estados de seguimiento de la bandeja (campo leads.inbox_status). Vacío = nuevo.
type InboxStatus = 'nuevo' | 'contactado' | 'descartado';
const STATUS_ORDER: InboxStatus[] = ['nuevo', 'contactado', 'descartado'];
const STATUS_LABELS: Record<InboxStatus, string> = {
  nuevo: 'Nueva',
  contactado: 'Contactada',
  descartado: 'Descartada',
};
function leadStatus(lead: Lead): InboxStatus {
  const s = lead.inbox_status;
  return s === 'contactado' || s === 'descartado' ? s : 'nuevo';
}

// Orígenes técnicos → etiqueta legible para el filtro. `form:<slug>` es el más
// común en tenants web-cms.
function sourceLabel(source: string): string {
  if (source.startsWith('form:')) return `Formulario · ${source.slice(5)}`;
  const MAP: Record<string, string> = {
    chatbot_web: 'Chatbot',
    booking: 'Agenda',
    whatsapp: 'WhatsApp',
    manual: 'Manual',
  };
  return MAP[source] ?? source;
}

export const Route = createFileRoute('/_authed/oportunidades-bandeja')({
  component: OportunidadesBandejaPage,
});

// Bandeja de oportunidades minimal (ADR 2026-05-21, Q1): para tenants que
// capturan oportunidades por formulario pero NO tienen CRM completo (capability
// leads_inbox sin crm). Lista simple + drawer de detalle de SOLO LECTURA con el
// mensaje y los campos que envió el contacto — sin kanban ni edición.
//
// Aunque no hay pipeline, la bandeja da gestión real: filtro por período,
// búsqueda, borrado (con confirmación) y export a CSV (selección o todo).

function OportunidadesBandejaPage() {
  const { selectedTenant } = useTenantContext();
  const tenantId = useActiveTenantId();
  const confirm = useConfirm();
  const deleteMut = useDeleteLead();

  const [period, setPeriod] = useDashboardPeriod();
  const timeZone = tenantTimeZone(selectedTenant);
  const resolved = useMemo(() => resolvePeriod(period, { timeZone }), [period, timeZone]);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | InboxStatus>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Lead | null>(null);
  // Selección para acciones en lote (id → Lead), preservada entre páginas.
  const [checked, setChecked] = useState<Record<string, Lead>>({});
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canDelete = can(currentUser()?.role, 'deleteRecords');
  const { data: sources } = useDistinctLeadSources();

  const listParams = {
    page,
    perPage: 25,
    sort: '-created',
    search: search.trim() || undefined,
    source: sourceFilter || undefined,
    inboxStatus: statusFilter || undefined,
    createdFrom: resolved.start,
    createdTo: resolved.end,
  };
  const { data, isLoading, isError } = useLeadsList(listParams);

  // Volver a la página 1 cuando cambia cualquier filtro (evita quedar en una
  // página que ya no existe con el nuevo rango).
  useEffect(() => {
    setPage(1);
  }, [period, search, sourceFilter, statusFilter]);

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalItems = data?.totalItems ?? 0;

  const checkedIds = Object.keys(checked);
  const checkedCount = checkedIds.length;
  const allPageChecked = items.length > 0 && items.every((l) => checked[l.id]);

  const toggleOne = (lead: Lead) =>
    setChecked((prev) => {
      const next = { ...prev };
      if (next[lead.id]) delete next[lead.id];
      else next[lead.id] = lead;
      return next;
    });

  const togglePage = () =>
    setChecked((prev) => {
      const next = { ...prev };
      if (allPageChecked) items.forEach((l) => delete next[l.id]);
      else items.forEach((l) => (next[l.id] = l));
      return next;
    });

  async function handleDeleteOne(lead: Lead) {
    const name = leadName(lead);
    const ok = await confirm({
      title: '¿Eliminar esta oportunidad?',
      description: `Se eliminará "${name}" de forma permanente. Esta acción no se puede deshacer.`,
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    setError(null);
    try {
      await deleteMut.mutateAsync({ leadId: lead.id });
      setChecked((prev) => {
        const next = { ...prev };
        delete next[lead.id];
        return next;
      });
      if (selected?.id === lead.id) setSelected(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No pudimos eliminar la oportunidad.');
    }
  }

  async function handleDeleteSelected() {
    if (checkedCount === 0) return;
    const ok = await confirm({
      title: `¿Eliminar ${checkedCount} oportunidad${checkedCount > 1 ? 'es' : ''}?`,
      description: 'Se eliminarán de forma permanente. Esta acción no se puede deshacer.',
      confirmLabel: `Eliminar ${checkedCount}`,
      tone: 'danger',
    });
    if (!ok) return;
    setError(null);
    try {
      // Secuencial: evita colisiones de requestKey del SDK y da un orden claro.
      for (const id of checkedIds) {
        await deleteMut.mutateAsync({ leadId: id });
      }
      setChecked({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No pudimos eliminar algunas oportunidades.');
    }
  }

  async function handleExport() {
    setError(null);
    setExporting(true);
    try {
      // Si hay selección, exporta solo eso; si no, todo lo filtrado.
      const rows: Lead[] =
        checkedCount > 0
          ? checkedIds.map((id) => checked[id]).filter(Boolean)
          : await fetchLeadsForExport(tenantId!, listParams);
      const csv = leadsToCsv(rows);
      downloadCsv(
        csv,
        exportFilename(selectedTenant?.slug || selectedTenant?.name || 'oportunidades'),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No pudimos generar el CSV.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.headerRow}>
        <div>
          <h1 className={styles.title}>Oportunidades</h1>
          <p className={styles.subtitle}>
            Contactos que llegaron por tus formularios
            {selectedTenant?.name ? ` · ${selectedTenant.name}` : ''}.
          </p>
        </div>
        <div className={styles.headerActions}>
          <PeriodFilter value={period} onChange={setPeriod} />
        </div>
      </header>

      {/* Toolbar: búsqueda + filtros + acciones */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={15} aria-hidden />
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Buscar por nombre, email, teléfono…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.filters}>
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | InboxStatus)}
            aria-label="Filtrar por estado"
          >
            <option value="">Todos los estados</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          {(sources?.length ?? 0) > 1 && (
            <select
              className={styles.filterSelect}
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              aria-label="Filtrar por origen"
            >
              <option value="">Todos los orígenes</option>
              {sources!.map((s) => (
                <option key={s.source} value={s.source}>
                  {sourceLabel(s.source)} ({s.count})
                </option>
              ))}
            </select>
          )}
        </div>
        <div className={styles.toolbarRight}>
          <span className={styles.count}>
            {isLoading ? '…' : `${totalItems} oportunidad${totalItems === 1 ? '' : 'es'}`}
          </span>
          <button
            type="button"
            className="btn"
            onClick={handleExport}
            disabled={exporting || (checkedCount === 0 && totalItems === 0)}
          >
            <Download size={15} aria-hidden />
            {exporting
              ? 'Generando…'
              : checkedCount > 0
                ? `Descargar selección (${checkedCount})`
                : 'Descargar CSV'}
          </button>
        </div>
      </div>

      {/* Barra de acciones en lote — solo con selección */}
      {canDelete && checkedCount > 0 && (
        <div className={styles.bulkBar}>
          <span>
            {checkedCount} seleccionada{checkedCount > 1 ? 's' : ''}
          </span>
          <div className={styles.bulkActions}>
            <button type="button" className={styles.linkBtn} onClick={() => setChecked({})}>
              Quitar selección
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={handleDeleteSelected}
              disabled={deleteMut.isPending}
            >
              <Trash2 size={15} aria-hidden />
              Eliminar {checkedCount}
            </button>
          </div>
        </div>
      )}

      {error && <div className={styles.errorBanner}>{error}</div>}

      {isLoading && <div className={styles.empty}>Cargando oportunidades…</div>}

      {!isLoading && isError && (
        <div className={styles.empty}>
          <p>No pudimos cargar las oportunidades. Recarga la página o intenta más tarde.</p>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div className={styles.empty}>
          <Users size={26} aria-hidden style={{ opacity: 0.4, marginBottom: 8 }} />
          <p>
            {search.trim() || sourceFilter || statusFilter
              ? 'Ninguna oportunidad coincide con los filtros en este período.'
              : 'No hay oportunidades en este período. Ajusta el filtro de fecha o espera a que llegue un formulario.'}
          </p>
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                {canDelete && (
                  <th className={styles.checkCol}>
                    <input
                      type="checkbox"
                      checked={allPageChecked}
                      onChange={togglePage}
                      aria-label="Seleccionar todas las de esta página"
                    />
                  </th>
                )}
                <th>Contacto</th>
                <th>Estado</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Origen</th>
                <th>Fecha</th>
                <th aria-hidden></th>
              </tr>
            </thead>
            <tbody>
              {items.map((lead) => {
                const c = lead.expand?.contact;
                const name = leadName(lead);
                return (
                  <tr key={lead.id} className={styles.row} onClick={() => setSelected(lead)}>
                    {canDelete && (
                      <td className={styles.checkCol} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!checked[lead.id]}
                          onChange={() => toggleOne(lead)}
                          aria-label={`Seleccionar ${name}`}
                        />
                      </td>
                    )}
                    <td className={styles.name}>
                      {name}
                      {c?.company ? <span className={styles.company}> · {c.company}</span> : null}
                    </td>
                    <td>
                      <StatusBadge status={leadStatus(lead)} />
                    </td>
                    <td className="subtle">{c?.email || '—'}</td>
                    <td className="subtle">{c?.phone || '—'}</td>
                    <td className="subtle">{lead.source || '—'}</td>
                    <td className="subtle">{formatDate(lead.created)}</td>
                    <td className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
                      <div className={styles.rowActions}>
                        <button
                          type="button"
                          className={styles.viewBtn}
                          onClick={() => setSelected(lead)}
                          aria-label={`Ver detalle de ${name}`}
                        >
                          Ver <ChevronRight size={14} aria-hidden />
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className={styles.deleteBtn}
                            onClick={() => handleDeleteOne(lead)}
                            aria-label={`Eliminar ${name}`}
                            title="Eliminar"
                          >
                            <Trash2 size={15} aria-hidden />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className={styles.pager}>
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <span className="subtle">
                Página {page} de {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}

      <DetailDrawer lead={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

// ── Drawer de detalle (solo lectura) ─────────────────────────────────────────

function DetailDrawer({ lead, onClose }: { lead: Lead | null; onClose: () => void }) {
  return (
    <Dialog.Root
      open={!!lead}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.panel} aria-describedby={undefined}>
          {lead && <DetailBody lead={lead} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DetailBody({ lead }: { lead: Lead }) {
  const c = lead.expand?.contact;
  const name = leadName(lead);
  const message = getMessage(lead);
  const extra = formExtraFields(lead);
  const update = useUpdateLead();

  // Estado + nota se editan localmente y se persisten (status al instante,
  // nota al salir del textarea si cambió). Seed desde el lead del snapshot.
  const [status, setStatus] = useState<InboxStatus>(leadStatus(lead));
  const [note, setNote] = useState(lead.inbox_note ?? '');
  const [savedNote, setSavedNote] = useState(false);

  async function changeStatus(next: InboxStatus) {
    if (next === status) return;
    const prev = status;
    setStatus(next);
    try {
      await update.mutateAsync({ leadId: lead.id, patch: { inbox_status: next } });
    } catch {
      setStatus(prev); // revertir si falla
    }
  }

  async function saveNote() {
    const trimmed = note.trim();
    if (trimmed === (lead.inbox_note ?? '').trim()) return;
    try {
      await update.mutateAsync({ leadId: lead.id, patch: { inbox_note: trimmed } });
      setSavedNote(true);
      setTimeout(() => setSavedNote(false), 2000);
    } catch {
      /* el banner de error global de la lista no aplica acá; silencioso */
    }
  }

  return (
    <>
      <div className={styles.panelHeader}>
        <div>
          <Dialog.Title className={styles.panelTitle}>{name}</Dialog.Title>
          {c?.company ? <p className={styles.panelSub}>{c.company}</p> : null}
        </div>
        <Dialog.Close className={styles.closeButton} aria-label="Cerrar">
          <X size={18} aria-hidden />
        </Dialog.Close>
      </div>

      <div className={styles.panelBody}>
        {/* Estado de seguimiento */}
        <section className={styles.section}>
          <h3 className={styles.sectionLabel}>Estado</h3>
          <div className={styles.statusGroup} role="group" aria-label="Estado de la oportunidad">
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                type="button"
                className={
                  s === status
                    ? `${styles.statusOption} ${styles[`statusOption__${s}`]}`
                    : styles.statusOption
                }
                aria-pressed={s === status}
                onClick={() => changeStatus(s)}
                disabled={update.isPending}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </section>

        {(c?.email || c?.phone) && (
          <section className={styles.section}>
            <h3 className={styles.sectionLabel}>Contacto</h3>
            {c?.email ? (
              <div className={styles.field}>
                <Mail size={15} aria-hidden />
                <a href={`mailto:${c.email}`}>{c.email}</a>
              </div>
            ) : null}
            {c?.phone ? (
              <div className={styles.field}>
                <Phone size={15} aria-hidden />
                <a href={`tel:${c.phone}`}>{c.phone}</a>
              </div>
            ) : null}
          </section>
        )}

        {message ? (
          <section className={styles.section}>
            <h3 className={styles.sectionLabel}>Mensaje</h3>
            <p className={styles.message}>{message}</p>
          </section>
        ) : null}

        {extra.length > 0 && (
          <section className={styles.section}>
            <h3 className={styles.sectionLabel}>Datos del formulario</h3>
            {extra.map(([k, v]) => (
              <div key={k} className={styles.kv}>
                <span className={styles.kvKey}>{fieldLabel(k)}</span>
                <span className={styles.kvVal}>{displayValue(v)}</span>
              </div>
            ))}
          </section>
        )}

        <section className={styles.section}>
          <h3 className={styles.sectionLabel}>Origen</h3>
          <div className={styles.kv}>
            <span className={styles.kvKey}>Canal</span>
            <span className={styles.kvVal}>{lead.source || '—'}</span>
          </div>
          <div className={styles.kv}>
            <span className={styles.kvKey}>Recibido</span>
            <span className={styles.kvVal}>{formatDateTime(lead.created)}</span>
          </div>
        </section>

        {/* Nota interna */}
        <section className={styles.section}>
          <h3 className={styles.sectionLabel}>
            Nota interna
            {savedNote && <span className={styles.savedHint}> · Guardada</span>}
          </h3>
          <textarea
            className={styles.noteInput}
            placeholder="Anota lo que necesites sobre esta oportunidad (solo tú y tu equipo la ven)."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={saveNote}
            rows={4}
          />
        </section>
      </div>
    </>
  );
}

function StatusBadge({ status }: { status: InboxStatus }) {
  return (
    <span className={`${styles.badge} ${styles[`badge__${status}`]}`}>{STATUS_LABELS[status]}</span>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function leadName(lead: Lead): string {
  const c = lead.expand?.contact;
  return [c?.first_name, c?.last_name].filter(Boolean).join(' ') || 'Oportunidad';
}

/**
 * Parsea un campo JSON de PB que puede llegar como objeto O como string. El hook
 * `forms_submit` guarda `custom_fields` con `JSON.stringify(...)`, así que el SDK
 * lo devuelve serializado; otros caminos lo entregan ya parseado. Manejamos ambos
 * (mismo gotcha que `parseTags`).
 */
function parseObj(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const p: unknown = JSON.parse(raw);
      return p && typeof p === 'object' ? (p as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  return {};
}

function getMessage(lead: Lead): string {
  const cf = parseObj(lead.custom_fields);
  const direct = typeof cf._form_message === 'string' ? cf._form_message : '';
  if (direct.trim()) return direct.trim();
  const fv = parseObj(cf._form_values);
  return typeof fv.message === 'string' ? fv.message.trim() : '';
}

// Campos del form que NO se repiten en "Datos del formulario" (ya van en el
// título, Contacto o Mensaje). `_hp` es el honeypot anti-spam.
const HANDLED_FIELDS = new Set([
  'first_name',
  'last_name',
  'name',
  'email',
  'phone',
  'message',
  '_hp',
]);

function formExtraFields(lead: Lead): [string, unknown][] {
  const fv = parseObj(parseObj(lead.custom_fields)._form_values);
  return Object.entries(fv).filter(
    ([k, v]) => !HANDLED_FIELDS.has(k) && String(v ?? '').trim() !== '',
  );
}

const FIELD_LABELS: Record<string, string> = {
  service: 'Servicio de interés',
  subject: 'Asunto',
  company: 'Empresa',
  budget: 'Presupuesto',
  city: 'Ciudad',
};

function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key.replace(/[_-]+/g, ' ').replace(/^\w/, (ch) => ch.toUpperCase());
}

function displayValue(val: unknown): string {
  const s = String(val ?? '').trim();
  // Valores tipo slug ("camaras-seguridad") → legibles ("Camaras seguridad").
  if (/^[a-z0-9]+([_-][a-z0-9]+)+$/.test(s)) {
    return s.replace(/[_-]+/g, ' ').replace(/^\w/, (ch) => ch.toUpperCase());
  }
  return s;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Export CSV ───────────────────────────────────────────────────────────────

function csvCell(val: unknown): string {
  const s = String(val ?? '');
  // Comillas dobladas + envoltura si hay coma, comilla o salto de línea.
  const escaped = s.replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${escaped}"` : escaped;
}

function leadsToCsv(rows: Lead[]): string {
  const header = [
    'Nombre',
    'Empresa',
    'Email',
    'Teléfono',
    'Origen',
    'Estado',
    'Mensaje',
    'Nota',
    'Fecha',
  ];
  const lines = rows.map((lead) => {
    const c = lead.expand?.contact;
    return [
      leadName(lead),
      c?.company ?? '',
      c?.email ?? '',
      c?.phone ?? '',
      lead.source ?? '',
      STATUS_LABELS[leadStatus(lead)],
      getMessage(lead),
      lead.inbox_note ?? '',
      formatDateTime(lead.created),
    ]
      .map(csvCell)
      .join(',');
  });
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
}

function exportFilename(base: string): string {
  const slug = base
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const stamp = new Date().toISOString().slice(0, 10);
  return `oportunidades-${slug || 'export'}-${stamp}.csv`;
}

function downloadCsv(csv: string, filename: string): void {
  // BOM para que Excel abra UTF-8 correctamente (acentos).
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
